import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  ArrowLeft, ArrowRight, Check, Loader2, Search, Compass,
  Cpu, Rocket, ChevronRight, Quote, Zap, Target, Users,
  Lightbulb, Shield, Clock, Star, Award, Download, Sparkles
} from 'lucide-react';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The 4 stages of the Pivot To Power Framework
const STAGES = [
  {
    id: 'assess', num: '01', label: 'ASSESS', title: 'Where Are You Now?',
    tagline: 'You cannot design a destination if you do not know your starting point.',
    icon: Search, color: 'purple',
    bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-300',
    badgeBg: 'bg-purple-100', badgeText: 'text-purple-700',
    gradientFrom: 'from-purple-600', gradientTo: 'to-purple-800',
    questions: [
      {
        key: 'automatable_tasks',
        prompt: 'What tasks in your current role could be fully automated by AI within the next two years?',
        hint: 'Be honest. Think about the tasks that fill your day — emails, scheduling, data entry, reports. What could a machine handle?',
        placeholder: 'List the tasks you do regularly that don\'t require your unique human judgment...'
      },
      {
        key: 'unique_value',
        prompt: 'What would remain if those tasks were gone? What is the uniquely human value you bring?',
        hint: 'When have you been most irreplaceable? What made you irreplaceable in that moment?',
        placeholder: 'The value I bring that no algorithm can replicate is...'
      },
      {
        key: 'cultural_knowledge',
        prompt: 'What cultural knowledge do you carry that most of your colleagues do not?',
        hint: 'What community or market do you understand better than any AI model currently does?',
        placeholder: 'I understand deeply...'
      },
      {
        key: 'cultural_premium',
        prompt: 'Describe your Cultural Premium — the unique value you carry — in 30 seconds.',
        hint: '"Your Cultural Premium is the unique, non-replicable value derived from your lived experience, intuition, and cultural fluency." — Pivot To Power',
        placeholder: 'My Cultural Premium is...'
      }
    ]
  },
  {
    id: 'architect', num: '02', label: 'ARCHITECT', title: 'Design Your Destination',
    tagline: 'You must design the destination before you build the road.',
    icon: Compass, color: 'pink',
    bg: 'bg-pink-50', border: 'border-pink-200', accent: 'text-pink-300',
    badgeBg: 'bg-pink-100', badgeText: 'text-pink-700',
    gradientFrom: 'from-pink-600', gradientTo: 'to-pink-800',
    questions: [
      {
        key: 'future_vision',
        prompt: 'What does your future look like? Describe success in 90 days, 1 year, and 5 years.',
        hint: 'Be specific and measurable. Not "I want to be successful" but "I want to be running a consulting firm with 3 clients."',
        placeholder: '90 days: ...\n1 year: ...\n5 years: ...'
      },
      {
        key: 'building_for',
        prompt: 'Who are you building for beyond yourself? Name them specifically.',
        hint: '"Your Legacy Blueprint is a purpose that extends beyond individual income to generational impact."',
        placeholder: 'I am building this for...'
      },
      {
        key: 'infrastructure',
        prompt: 'What infrastructure could you create that would outlast your direct involvement?',
        hint: 'Think about systems, frameworks, businesses, communities, or content that will keep working without you.',
        placeholder: 'The infrastructure I want to build is...'
      },
      {
        key: 'business_idea',
        prompt: 'What business idea or venture have you been sitting on because you thought you lacked the resources?',
        hint: '"The cost of starting is lower than ever. The cost of waiting is higher than ever."',
        placeholder: 'The idea I\'ve been holding back is...'
      }
    ]
  },
  {
    id: 'automate', num: '03', label: 'AUTOMATE', title: 'Remove the Friction',
    tagline: 'Every hour bought back through automation can be invested in your Cultural Premium.',
    icon: Cpu, color: 'amber',
    bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-300',
    badgeBg: 'bg-amber-100', badgeText: 'text-amber-700',
    gradientFrom: 'from-amber-600', gradientTo: 'to-amber-800',
    questions: [
      {
        key: 'high_leverage_tasks',
        prompt: 'What are the 3 highest-leverage tasks in your current work — the ones that only you can do well?',
        hint: 'These are the tasks where your judgment, creativity, and Cultural Premium matter most.',
        placeholder: '1. \n2. \n3. '
      },
      {
        key: 'low_leverage_tasks',
        prompt: 'What are the 3 lowest-leverage tasks that drain your time and could be automated?',
        hint: 'Be ruthless. Every minute spent on automatable work is a minute stolen from your highest value.',
        placeholder: '1. \n2. \n3. '
      },
      {
        key: 'freed_time',
        prompt: 'What would you do with 5 extra hours per week if you automated your lowest-leverage tasks?',
        hint: 'This is where transformation lives — in the space you create.',
        placeholder: 'With 5 freed hours, I would...'
      },
      {
        key: 'first_automation',
        prompt: 'What is the first task you will automate this week? Name the tool and set a deadline.',
        hint: '"Progress over perfection." Start now. Iterate later.',
        placeholder: 'This week I will automate [task] using [tool] by [day]...'
      }
    ]
  },
  {
    id: 'accelerate', num: '04', label: 'ACCELERATE', title: 'Scale Your Power',
    tagline: 'This is where exponential growth happens — but only after the first three stages are complete.',
    icon: Rocket, color: 'emerald',
    bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-300',
    badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-700',
    gradientFrom: 'from-emerald-600', gradientTo: 'to-emerald-800',
    questions: [
      {
        key: 'mvp',
        prompt: 'What is the minimum viable version of your business or project you could build without outside funding?',
        hint: '"AI is your co-founder. It gives you the strategist, the marketer, and the analyst without the overhead."',
        placeholder: 'The simplest version of my idea that I could launch is...'
      },
      {
        key: 'proof_of_concept',
        prompt: 'What proof of concept could you build in the next 30 days that would change your trajectory?',
        hint: 'One proof of concept is worth a thousand business plans.',
        placeholder: 'In the next 30 days, I will build...'
      },
      {
        key: 'generational_wealth',
        prompt: 'What does generational wealth mean specifically for your family?',
        hint: '"Generational wealth is not just money. It is infrastructure. It is access. It is a foundation the next generation builds upon."',
        placeholder: 'For my family, generational wealth means...'
      },
      {
        key: 'wealth_vs_income',
        prompt: 'What is the difference between the income you earn and the wealth you are building? Are you building wealth?',
        hint: 'Income pays bills. Wealth builds futures. Which one are you focused on?',
        placeholder: 'The honest truth about my income vs. wealth is...'
      }
    ]
  }
];

export default function LifestyleDesigner() {
  const navigate = useNavigate();
  const [view, setView] = useState('intro'); // intro | journey | blueprint
  const [stageIdx, setStageIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);
  const [existingJourney, setExistingJourney] = useState(null);
  const [aiHelping, setAiHelping] = useState(false);
  const blueprintRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  // Initialize answers object
  const [answers, setAnswers] = useState(() => {
    const init = {};
    STAGES.forEach(s => {
      init[s.id] = {};
      s.questions.forEach(q => { init[s.id][q.key] = ''; });
    });
    return init;
  });

  useEffect(() => {
    loadExisting();
  }, []);

  const loadExisting = async () => {
    try {
      const res = await axios.get(`${API}/pivot-journey/${user.user_id}`);
      if (res.data.status !== 'not_found' && res.data.stages) {
        setExistingJourney(res.data);
        // Populate answers from existing journey
        const loaded = { ...answers };
        STAGES.forEach(s => {
          if (res.data.stages[s.id]) {
            loaded[s.id] = { ...loaded[s.id], ...res.data.stages[s.id] };
          }
        });
        setAnswers(loaded);
        if (res.data.completed_at) {
          setView('blueprint');
        }
      }
    } catch (e) { /* no existing journey */ }
    setInitialLoading(false);
  };

  const animateTransition = (cb) => {
    setFadeIn(false);
    setTimeout(() => { cb(); setFadeIn(true); }, 200);
  };

  const handleAiHelp = async () => {
    if (!currentQuestion) return;
    setAiHelping(true);
    try {
      const res = await axios.post(`${API}/ai/chat`, {
        user_id: user.user_id,
        message: `I'm working on a Pivot To Power assessment. The question is: "${currentQuestion.prompt}". The hint says: "${currentQuestion.hint}". Help me think through this — give me 2-3 thought-provoking examples or angles to consider. Be brief and direct. Don't answer for me, help me think.`,
        session_id: 'journey-help'
      });
      const helpText = res.data.response || 'Think about specific moments in your career where you made a unique impact...';
      setAnswers(prev => ({
        ...prev,
        [currentStage.id]: { ...prev[currentStage.id], [currentQuestion.key]: prev[currentStage.id][currentQuestion.key] + (prev[currentStage.id][currentQuestion.key] ? '\n\n--- AI Thought Starter ---\n' : '') + helpText }
      }));
      toast.success('AI suggestion added to your answer');
    } catch (e) {
      toast.error('Could not get AI help right now');
    }
    setAiHelping(false);
  };

  const currentStage = STAGES[stageIdx];
  const currentQuestion = currentStage?.questions[questionIdx];
  const totalQuestions = STAGES.reduce((sum, s) => sum + s.questions.length, 0);
  const completedQuestions = STAGES.slice(0, stageIdx).reduce((sum, s) => sum + s.questions.length, 0) + questionIdx;

  const currentAnswer = currentStage ? (answers[currentStage.id][currentQuestion?.key] || '') : '';

  const handleNext = () => {
    if (questionIdx < currentStage.questions.length - 1) {
      animateTransition(() => setQuestionIdx(questionIdx + 1));
    } else if (stageIdx < STAGES.length - 1) {
      // Save stage progress
      saveStage(currentStage.id);
      animateTransition(() => { setStageIdx(stageIdx + 1); setQuestionIdx(0); });
    } else {
      // Final submit
      handleComplete();
    }
  };

  const handleBack = () => {
    if (questionIdx > 0) {
      animateTransition(() => setQuestionIdx(questionIdx - 1));
    } else if (stageIdx > 0) {
      animateTransition(() => {
        const prevStage = STAGES[stageIdx - 1];
        setStageIdx(stageIdx - 1);
        setQuestionIdx(prevStage.questions.length - 1);
      });
    }
  };

  const saveStage = async (stageId) => {
    try {
      await axios.post(`${API}/pivot-journey/save-stage`, {
        user_id: user.user_id, stage: stageId, data: answers[stageId]
      });
    } catch (e) { /* silent save */ }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/pivot-journey/complete`, {
        user_id: user.user_id, ...answers
      });
      toast.success('Your Pivot Blueprint is ready!');
      animateTransition(() => setView('blueprint'));
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  const downloadBlueprintPDF = async () => {
    if (!blueprintRef.current) return;
    toast.info('Generating PDF...');
    try {
      const canvas = await html2canvas(blueprintRef.current, { scale: 2, backgroundColor: '#f9fafb', useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = pdfHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }
      pdf.save('Pivot-To-Power-Blueprint.pdf');
      toast.success('Blueprint PDF downloaded!');
    } catch (e) { toast.error('PDF generation failed'); }
  };

  if (initialLoading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
        </div>
      </AppShell>
    );
  }

  // ──────────────── INTRO PAGE ────────────────
  if (view === 'intro') {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
          {/* Hero */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center space-x-2 bg-purple-500/20 text-purple-300 border border-purple-500/20 px-4 py-1.5 rounded-full text-xs font-inter font-semibold uppercase tracking-wider mb-6">
              <Zap className="w-3.5 h-3.5" />
              <span>The Framework</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-cormorant font-bold text-white mb-4 leading-tight" data-testid="pivot-journey-title">
              Pivot To Power<br />
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400 bg-clip-text text-transparent">Journey</span>
            </h1>
            <p className="text-[#A39BB0] font-inter text-lg max-w-2xl mx-auto leading-relaxed">
              Discover your purpose. Architect your future. Automate the friction. Accelerate into your power.
            </p>
          </div>

          {/* Quote */}
          <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-8 mb-12 text-center" data-testid="book-quote">
            <Quote className="w-8 h-8 text-purple-400/50 mx-auto mb-3" />
            <p className="text-xl font-cormorant italic text-white/80 leading-relaxed max-w-2xl mx-auto">
              "You cannot design a destination if you do not know your starting point. And you cannot reach a destination you have never named."
            </p>
            <p className="text-gray-400 text-sm font-inter mt-3">— Pivot To Power</p>
          </div>

          {/* 4 Stages Visual */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {STAGES.map((stage, i) => {
              const Icon = stage.icon;
              const hasData = existingJourney?.stages?.[stage.id];
              return (
                <div key={stage.id} className={`relative bg-white/[0.07] backdrop-blur-xl rounded-2xl border p-6 text-center group hover:bg-white/[0.1] transition-all ${hasData ? stage.border : 'border-white/[0.08]'}`} data-testid={`stage-card-${stage.id}`}>
                  <div className={`w-12 h-12 bg-gradient-to-br ${stage.gradientFrom} ${stage.gradientTo} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-sm`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className={`text-xs font-inter font-bold tracking-wider mb-1 ${stage.accent}`}>STAGE {stage.num}</div>
                  <h3 className="text-lg font-cormorant font-bold text-white mb-2">{stage.label}</h3>
                  <p className="text-[#A39BB0] text-xs font-inter leading-relaxed">{stage.title}</p>
                  {hasData && (
                    <div className="absolute top-3 right-3">
                      <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center border border-emerald-500/30">
                        <Check className="w-3 h-3 text-emerald-400" />
                      </div>
                    </div>
                  )}
                  {i < STAGES.length - 1 && (
                    <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" />
                  )}
                </div>
              );
            })}
          </div>

          {/* What You'll Discover */}
          <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-8 mb-12">
            <h2 className="text-2xl font-cormorant font-bold text-white mb-6 text-center">What You'll Discover</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Search, text: 'Your Cultural Premium — the unique value AI cannot replicate', color: 'text-purple-400 bg-purple-500/20' },
                { icon: Compass, text: 'Your Legacy Blueprint — what you\'re building and for whom', color: 'text-pink-400 bg-pink-500/20' },
                { icon: Cpu, text: 'Your AI Leverage System — automate what holds you back', color: 'text-amber-400 bg-amber-500/20' },
                { icon: Rocket, text: 'Your Acceleration Strategy — scale what is working', color: 'text-emerald-400 bg-emerald-500/20' },
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-3 p-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.color.split(' ')[1]}`}>
                    <item.icon className={`w-4.5 h-4.5 ${item.color.split(' ')[0]}`} />
                  </div>
                  <p className="text-white/90 font-inter text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            {existingJourney?.completed_at ? (
              <div className="space-y-3">
                <button onClick={() => setView('blueprint')}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-10 py-4 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all text-lg"
                  data-testid="view-blueprint-button">
                  View Your Pivot Blueprint <ArrowRight className="w-5 h-5 inline ml-1" />
                </button>
                <div>
                  <button onClick={() => { setView('journey'); setStageIdx(0); setQuestionIdx(0); }}
                    className="text-purple-600 hover:text-purple-800 font-inter text-sm underline mt-2" data-testid="retake-journey-button">
                    Retake the Journey
                  </button>
                </div>
              </div>
            ) : existingJourney?.stages ? (
              <button onClick={() => { setView('journey'); }} 
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-10 py-4 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all text-lg"
                data-testid="continue-journey-button">
                Continue Your Journey <ArrowRight className="w-5 h-5 inline ml-1" />
              </button>
            ) : (
              <button onClick={() => setView('journey')}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-10 py-4 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all text-lg"
                data-testid="begin-pivot-button">
                Begin Your Pivot <ArrowRight className="w-5 h-5 inline ml-1" />
              </button>
            )}
            <p className="text-[#A39BB0] text-xs font-inter mt-4">Takes about 15-20 minutes of honest reflection</p>
          </div>
        </div>
      </AppShell>
    );
  }

  // ──────────────── JOURNEY FLOW ────────────────
  if (view === 'journey') {
    const isLastQuestion = stageIdx === STAGES.length - 1 && questionIdx === currentStage.questions.length - 1;
    const Icon = currentStage.icon;

    return (
      <AppShell>
        {/* Header */}
        <div className="border-b border-white/[0.06]">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button onClick={() => { saveStage(currentStage.id); setView('intro'); }} className="text-[#A39BB0] hover:text-white transition-colors" data-testid="back-to-intro">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className={`w-8 h-8 bg-gradient-to-br ${currentStage.gradientFrom} ${currentStage.gradientTo} rounded-lg flex items-center justify-center`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className={`text-[10px] font-inter font-bold uppercase tracking-wider ${currentStage.accent}`}>Stage {currentStage.num}</div>
                <div className="text-sm font-cormorant font-bold text-white">{currentStage.label}</div>
              </div>
            </div>
            <div className="text-[#A39BB0] text-xs font-inter">{completedQuestions + 1} / {totalQuestions}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-3xl mx-auto px-6 pt-4">
          {/* Stage indicators */}
          <div className="flex items-center space-x-1 mb-2">
            {STAGES.map((s, i) => (
              <div key={s.id} className="flex-1 flex space-x-0.5">
                {s.questions.map((_, qi) => {
                  const isComplete = i < stageIdx || (i === stageIdx && qi < questionIdx);
                  const isCurrent = i === stageIdx && qi === questionIdx;
                  return (
                    <div key={qi} className={`h-1.5 flex-1 rounded-full transition-all ${
                      isComplete ? `bg-gradient-to-r ${s.gradientFrom} ${s.gradientTo}` : isCurrent ? 'bg-white/40' : 'bg-white/10'
                    }`} />
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] font-inter text-[#A39BB0] mb-6">
            {STAGES.map(s => (
              <span key={s.id} className={s.id === currentStage.id ? `font-semibold ${s.accent}` : ''}>{s.label}</span>
            ))}
          </div>
        </div>

        {/* Question */}
        <div className={`max-w-3xl mx-auto px-6 transition-all duration-200 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-8" data-testid={`question-${currentStage.id}-${questionIdx}`}>
            <h2 className="text-xl sm:text-2xl font-cormorant font-bold text-white mb-3 leading-snug">
              {currentQuestion.prompt}
            </h2>
            <p className="text-white/70 text-sm font-inter mb-6 leading-relaxed">
              {currentQuestion.hint}
            </p>

            <button onClick={handleAiHelp} disabled={aiHelping}
              className="flex items-center space-x-2 text-amber-400 hover:text-amber-300 text-xs font-inter font-medium mb-4 bg-amber-500/10 border border-amber-500/20 px-4 py-2 rounded-full transition-all hover:bg-amber-500/15 disabled:opacity-50"
              data-testid="ai-help-button">
              {aiHelping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              <span>{aiHelping ? 'Thinking...' : 'Help me think through this'}</span>
            </button>

            <textarea
              value={currentAnswer}
              onChange={(e) => setAnswers(prev => ({
                ...prev,
                [currentStage.id]: { ...prev[currentStage.id], [currentQuestion.key]: e.target.value }
              }))}
              className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl px-5 py-4 text-white focus:border-purple-500/50 outline-none transition-all placeholder:text-[#A39BB0]/40 min-h-[160px] font-inter text-sm resize-none leading-relaxed"
              placeholder={currentQuestion.placeholder}
              data-testid="journey-answer-input"
              autoFocus
            />

            <div className="flex justify-between mt-6 pt-6 border-t border-white/[0.06]">
              <button onClick={handleBack}
                disabled={stageIdx === 0 && questionIdx === 0}
                className={`flex items-center space-x-2 font-inter text-sm py-3 px-6 rounded-full transition-all ${
                  stageIdx === 0 && questionIdx === 0 ? 'opacity-30 cursor-not-allowed text-[#A39BB0]' : 'text-[#A39BB0] hover:text-white hover:bg-white/10'
                }`} data-testid="journey-back-button">
                <ArrowLeft className="w-4 h-4" /><span>Back</span>
              </button>

              <button onClick={handleNext}
                disabled={!currentAnswer.trim() || loading}
                className={`flex items-center space-x-2 font-inter font-medium text-sm py-3 px-8 rounded-full transition-all ${
                  currentAnswer.trim()
                    ? `bg-gradient-to-r ${currentStage.gradientFrom} ${currentStage.gradientTo} text-white hover:shadow-lg hover:opacity-90`
                    : 'bg-white/5 text-[#A39BB0] cursor-not-allowed'
                }`} data-testid="journey-next-button">
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /><span>Creating Blueprint...</span></>
                ) : isLastQuestion ? (
                  <><span>Complete My Blueprint</span><Award className="w-4 h-4" /></>
                ) : (
                  <><span>Continue</span><ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // ──────────────── BLUEPRINT VIEW ────────────────
  if (view === 'blueprint') {
    const sections = [
      {
        stage: STAGES[0], title: 'Your Cultural Premium',
        subtitle: 'The unique value you carry that AI cannot replicate',
        keys: ['unique_value', 'cultural_knowledge', 'cultural_premium'],
        icon: Search
      },
      {
        stage: STAGES[1], title: 'Your Legacy Blueprint',
        subtitle: 'The destination you are building toward',
        keys: ['future_vision', 'building_for', 'infrastructure', 'business_idea'],
        icon: Compass
      },
      {
        stage: STAGES[2], title: 'Your AI Leverage System',
        subtitle: 'How you will automate friction and reclaim your time',
        keys: ['high_leverage_tasks', 'low_leverage_tasks', 'freed_time', 'first_automation'],
        icon: Cpu
      },
      {
        stage: STAGES[3], title: 'Your Acceleration Plan',
        subtitle: 'How you will scale your power and build generational wealth',
        keys: ['mvp', 'proof_of_concept', 'generational_wealth', 'wealth_vs_income'],
        icon: Rocket
      },
    ];

    return (
      <AppShell>
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-cormorant font-bold text-white mb-1" data-testid="blueprint-title">Your Pivot Blueprint</h1>
              <p className="text-[#A39BB0] font-inter text-sm">Your map. Your mirror. Review it often.</p>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => { setView('journey'); setStageIdx(0); setQuestionIdx(0); }}
                className="text-purple-300 hover:text-purple-200 text-xs font-inter font-medium bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-full transition-all border border-white/10" data-testid="retake-journey-link">
                Retake Journey
              </button>
              <button onClick={downloadBlueprintPDF}
                className="flex items-center space-x-1 bg-white/10 hover:bg-white/15 text-white font-inter text-xs font-medium px-3 py-1.5 rounded-full transition-all border border-white/10" data-testid="download-blueprint-pdf">
                <Download className="w-3.5 h-3.5" /><span>PDF</span>
              </button>
            </div>
          </div>

          {/* Blueprint Content — white for PDF capture */}
          <div ref={blueprintRef} className="bg-white rounded-2xl p-8" style={{ color: '#111827' }}>
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-200">
              <Award className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-cormorant font-bold text-gray-900 mb-2">Your Pivot Blueprint</h2>
            <p className="text-gray-500 font-inter text-sm max-w-lg mx-auto">
              Review it often. Update it as you grow. Share it with your Power Circle.
            </p>
          </div>

          {/* Blueprint Sections */}
          <div className="space-y-8 mb-12">
            {sections.map((section, i) => {
              const Icon = section.icon;
              const stageAnswers = answers[section.stage.id];
              return (
                <div key={i} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${section.stage.border}`} data-testid={`blueprint-section-${section.stage.id}`}>
                  <div className={`${section.stage.bg} px-8 py-5 flex items-center space-x-3 border-b ${section.stage.border}`}>
                    <div className={`w-10 h-10 bg-gradient-to-br ${section.stage.gradientFrom} ${section.stage.gradientTo} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-cormorant font-bold text-gray-900">{section.title}</h2>
                      <p className="text-gray-500 text-xs font-inter">{section.subtitle}</p>
                    </div>
                  </div>
                  <div className="p-8 space-y-6">
                    {section.keys.map(key => {
                      const question = section.stage.questions.find(q => q.key === key);
                      const answer = stageAnswers?.[key];
                      if (!answer) return null;
                      return (
                        <div key={key}>
                          <p className="text-gray-400 text-xs font-inter uppercase tracking-wider mb-2">{question?.prompt?.split('?')[0]}?</p>
                          <p className="text-gray-800 font-inter text-sm leading-relaxed whitespace-pre-wrap">{answer}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          </div>{/* close blueprintRef */}

          {/* CTA */}
          <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-8 text-center mt-6">
            <h3 className="text-2xl font-cormorant font-bold text-white mb-2">What's Next?</h3>
            <p className="text-[#A39BB0] font-inter text-sm mb-6">Your blueprint is your foundation. Now let's put it into action.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={() => navigate('/ai-guide')}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-8 py-3 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all"
                data-testid="blueprint-to-ai-guide">
                Talk to Power Guide <ArrowRight className="w-4 h-4 inline ml-1" />
              </button>
              <button onClick={() => navigate('/community')}
                className="bg-white/10 border border-white/10 text-white font-inter font-medium px-8 py-3 rounded-full hover:bg-white/15 transition-all"
                data-testid="blueprint-to-community">
                Join a Power Circle
              </button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return null;
}
